import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { IoMdClose } from "react-icons/io";
import fetchDataFromTelligent from "../commonFunction";
import { envConfig } from "../../utils/constants";
import { selectStoreConfigSelector } from "../../store/storeConfig/selector";
import { useAppSelector } from "../../hooks";
import Gallery from "../Gallery";
import "./style.css";
import { selectCartSelector } from "../../store/cart/selector";
import Button from "../button";
import { formatNumberWithCommas } from "../../utils/utility";

interface CardLayoutProps {
  id: number;
  price: number;
  mvp: number;
  custom: number;
  customPriceInfo?: string;
  customPackageLink?: string;
  name: string;
  description: string;
  currency: string;
  suffix: string;
  termDescription: string;
  features: Array<string>;
  selectCard: Function;
  trial_message_summarycard?: string;
  isSelected: string;
  sku: string;
  btnText: string;
  trial_duration: number;
  loader: boolean;
  maxLengthOfFeatures: number;
}

interface TabData {
  value: string;
  name: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface SelectedItem {
  id: string;
  name: string;
  type: string;
}

interface TabsDialogProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  subtitle: string;
  submitTitle: string;
  cancelTitle: string;
  currency: string;
  price: number;
  suffix: string;
  showFooter?: boolean;
  selectedItems: Array<SelectedItem>;
  selectItem: (item: SelectedItem) => void;
  tabsData: Array<TabData>;
  submit?: () => void;
  checkout?: () => void;
  virtualProductSku?: string;
  telligentData: Array<any>;
}

const TabsDialog = ({
  open,
  handleClose,
  title,
  subtitle,
  currency,
  price,
  suffix,
  showFooter = true,
  tabsData,
  selectItem,
  submit,
  checkout,
  virtualProductSku,
  telligentData,
}: TabsDialogProps) => {
  const [activeTab, setActiveTab] = useState<string>(
    tabsData[0]?.value || "defaultTab"
  );
  const storeData = useAppSelector(selectStoreConfigSelector);
  const [configLoader, setConfigLoader] = useState(false);
  const cartData = useAppSelector(selectCartSelector);

  useEffect(() => {
    if (tabsData.length > 0) {
      setActiveTab(tabsData[0].value);
    }
  }, [tabsData]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const filteredData = telligentData.filter(
    (data, index) => activeTab === tabsData[index]?.value
  );

  // Preprocess key benefits for current tab
  const currentDataIndex = tabsData.findIndex((tab) => tab.value === activeTab);
  const currentData = telligentData[currentDataIndex];

  let keyBenefitsArray: string[] = [];
  if (currentData?.keybenefits) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(currentData.keybenefits, "text/html");
    const listItems = doc.querySelectorAll("ol > li");
    keyBenefitsArray = Array.from(listItems).map(
      (item) => item.textContent?.trim() || ""
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      className="product-tabs-dialog-container"
    >
      <div className="dialog-header">
        <div className="dialog-title-container">
          <DialogTitle className="dialog-title">
            {title}
            <button
              data-track="close-button"
              className="close-btn"
              onClick={handleClose}
            >
              <IoMdClose className="close-icon" />
            </button>
            <Typography className="dialog-subtitle">{subtitle}</Typography>
          </DialogTitle>
        </div>
        <span className="dialog-price">
          {currency}
            {formatNumberWithCommas(price)}
           <span className="duration">/{suffix}</span>
        </span>
      </div>
      <div className="tabs-context">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{
                style: {
                  backgroundColor: "var(--active-tab-bottomborder)",
                  height: "4px",
                },
              }}
            >
              {tabsData.map((tab) => (
                <Tab
                  data-track={tab.value}
                  key={tab.value}
                  label={
                    <div className="custom-tab">
                      <span className="tab-label">{tab.name}</span>
                    </div>
                  }
                  value={tab.value}
                  disabled={tab.disabled}
                />
              ))}
            </Tabs>
          </div>

      <DialogContent className="dialog-content">
        <div className="tabs-container">
          <div className="tabs-content">
            <div className="gallery-container">
              {filteredData.length > 0 && (
                <Gallery productData={filteredData} />
              )}
            </div>

            <div className="description-container">
              <div className="tabs-product-description">
                {telligentData.length > 0 &&
                  telligentData.map(
                    (data, index) =>
                      activeTab === tabsData[index]?.value && (
                        <div key={data.pid}>
                          {data.shortdescription && (
                            <p
                              className="short-description"
                              dangerouslySetInnerHTML={{
                                __html: data.shortdescription,
                              }}
                            />
                          )}
                          {data.detaileddescription && (
                            <>
                              <h5 className="features">Features</h5>
                              <p
                                className="short-description"
                                dangerouslySetInnerHTML={{
                                  __html: data.detaileddescription,
                                }}
                              />
                            </>
                          )}
                        </div>
                      )
                  )}

                {keyBenefitsArray.length > 0 && (
                  <ul className="key-benefits-list">
                    {keyBenefitsArray.map((benefit, index) => (
                      <li key={index} className="key-benefit-item">
                        <span className="keybenefits"></span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TabsDialog;
